import React, { useEffect, useRef, useState } from 'react'
import {
  CompanyId,
  CustomType,
  DeleteGoal$Request,
  GoalWithProgress,
  LeaveGoal$Request,
  Role,
} from '../../services/api.types'
import YumButton from '../YumButton/YumButton'
import { Capacitor } from '@capacitor/core'
import { jt, t } from 'ttag'
import { ActionSheet, ActionSheetButtonStyle } from '@capacitor/action-sheet'
import { store } from '../../index'
import { push } from 'connected-react-router'
import { darkGreen, primaryBlue, red } from '../../styles/material-ui-theme-v2'
import { IJoinGoalPayload } from '../../views/Activity/store/ActivitySagas'
import dayjs from '../../dayjsConfig'
import { convertUTCToLocalDate } from '../../common/helpers/date'
import YumDialogTitle from '../YumDialogTitle/YumDialogTitle'
import YumDialogContent from '../YumDialogContent/YumDialogContent'
import YumDialogContentText from '../YumDialogContentText/YumDialogContentText'
import YumDialogActions from '../YumDialogActions/YumDialogActions'
import YumCancelButton from '../YumCancelButton/YumCancelButton'
import YumSubmitButton from '../YumSubmitButton/YumSubmitButton'
import YumDialog from '../YumDialog/YumDialog'

import { ReactComponent as LogOutIcon } from '../../assets/v2/icons/log-out.svg'
import { ReactComponent as EditIcon } from '../../assets/icons/edit.svg'
import { ReactComponent as DeleteIcon } from '../../assets/icons/trash.svg'
import { ReactComponent as AddIcon } from '../../assets/v3/icons/add.svg'
import { ReactComponent as MoreIcon } from '../../assets/v3/icons/more.svg'

export interface IYumChallengeTabsStateProps {
  className?: string
  goal: GoalWithProgress
  isListView?: boolean
  hasRoleInCompany(companyId: CompanyId, role: Role): boolean
  joinInProgress: boolean
}

export interface IYumChallengeTabsDispatchProps {
  joinGoal(payload: IJoinGoalPayload): void
  leaveGoal(payload: LeaveGoal$Request): void
  deleteGoal(payload: DeleteGoal$Request): void
}

type IYumChallengeTabsProps = IYumChallengeTabsStateProps & IYumChallengeTabsDispatchProps

export default function YumChallengeActions(props: IYumChallengeTabsProps) {
  const { className, goal, isListView, hasRoleInCompany, joinGoal, leaveGoal, deleteGoal, joinInProgress } = props
  const {
    id: goalId,
    companyId,
    companyName,
    title,
    endDate,
    progressType,
    customType,
    participating,
    manualEntryEnabled,
  } = goal
  const challengeName = <strong key={'title'}>{title}</strong>

  const [contextMenuVisible, setContextMenuVisible] = useState(false)
  const [leaveGoalModalVisible, setLeaveGoalModalVisible] = useState(false)
  const [deleteGoalModalVisible, setDeleteGoalModalVisible] = useState(false)

  const menuRef = useRef<HTMLUListElement | null>(null)
  const triggerRef = useRef<HTMLElement | null>(null)

  // Move focus into the dropdown when it opens so keyboard/SR users land on the
  // first action instead of staying on the trigger (WCAG 2.4.3).
  useEffect(() => {
    if (contextMenuVisible) {
      menuRef.current?.querySelector('button')?.focus()
    }
  }, [contextMenuVisible])

  const closeContextMenuAndRestoreFocus = () => {
    setContextMenuVisible(false)
    triggerRef.current?.focus()
  }

  const onMenuKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.stopPropagation()
      closeContextMenuAndRestoreFocus()
    }
  }

  const isPresentOrFutureGoal = dayjs(convertUTCToLocalDate(new Date(endDate))).isAfter(new Date())
  const isUser = hasRoleInCompany(companyId, Role.USER)
  const isAdmin = hasRoleInCompany(companyId, Role.ADMIN)
  const actionsVisible = isAdmin || (isPresentOrFutureGoal && isUser)

  const challengeEndDate = dayjs(convertUTCToLocalDate(new Date(endDate)))
  const challengeHasNotEnded = !challengeEndDate.isBefore(new Date())

  const onJoinActionClicked = (event?: any) => {
    if (event) event.preventDefault()
    setContextMenuVisible(false)

    const isPoints = goal.customType === CustomType.POINTS
    const pointsLogic = isPoints ? JSON.parse(goal.pointsLogic) : null
    const challengeStartDate = dayjs(convertUTCToLocalDate(new Date(goal.startDate)))
    const challengeEndDate = dayjs(convertUTCToLocalDate(new Date(goal.endDate)))
    joinGoal({
      goalId,
      progressType,
      customType,
      manualEntryEnabled,

      // For refreshChallengeResults.
      isPoints,
      pointsLogic,
      startDateFormatted: challengeStartDate.locale('en').format('YYYY-MM-DD'),
      endDateFormatted: challengeEndDate.locale('en').format('YYYY-MM-DD'),
      isLegacyRanking: false,
    })
  }

  const toggleContextMenu = (event?: any) => {
    if (event) event.preventDefault()
    setContextMenuVisible(!contextMenuVisible)
  }
  const toggleLeaveGoalModal = () => setLeaveGoalModalVisible(!leaveGoalModalVisible)
  const toggleDeleteGoalModal = () => setDeleteGoalModalVisible(!deleteGoalModalVisible)

  const onLeaveGoalConfirmed = () => {
    toggleLeaveGoalModal()
    leaveGoal({ goalId })
  }

  const onLeaveGoalClicked = (event?: any) => {
    if (event) event.preventDefault()
    toggleContextMenu()
    toggleLeaveGoalModal()
  }

  const onDeleteGoalClicked = (event?: any) => {
    if (event) event.preventDefault()
    toggleContextMenu()
    toggleDeleteGoalModal()
  }

  const onDeleteGoalConfirmed = () => {
    toggleDeleteGoalModal()
    deleteGoal({ goalId })
  }

  const onBackdropClicked = (event: any) => {
    setLeaveGoalModalVisible(false)
    setDeleteGoalModalVisible(false)
  }

  const showJoinBtn = !participating && isUser && challengeHasNotEnded
  const showLeaveBtn = participating && challengeHasNotEnded
  const joinOrLeaveBtnVisible = showJoinBtn || showLeaveBtn

  const onContextMenuClicked = (event: any) => {
    event.preventDefault()
    // Remember the trigger so Escape can return focus to it.
    triggerRef.current = event.currentTarget as HTMLElement

    if (Capacitor.getPlatform() === 'web') {
      return toggleContextMenu()
    }

    try {
      const showActions = async () => {
        const options = []
        if (showJoinBtn) {
          options.push({
            title: t`Join`,
            style: ActionSheetButtonStyle.Default,
          })
        }
        if (showLeaveBtn) {
          options.push({
            title: t`Leave challenge`,
            style: isAdmin ? ActionSheetButtonStyle.Default : ActionSheetButtonStyle.Destructive,
          })
        }
        if (isAdmin) {
          options.push({
            title: t`Edit challenge`,
            style: ActionSheetButtonStyle.Default,
          })
        }
        if (isAdmin) {
          options.push({
            title: t`Delete challenge`,
            style: ActionSheetButtonStyle.Destructive,
          })
        }
        options.push({
          title: t`Cancel`,
          style: ActionSheetButtonStyle.Cancel,
        })
        const result = await ActionSheet.showActions({
          title: title,
          //message: companyName,
          options,
        })

        if (result) {
          if (showJoinBtn && result.index === 0) {
            onJoinActionClicked()
          }
          if (showLeaveBtn && result.index === 0) {
            toggleLeaveGoalModal()
          }
          if (
            isAdmin &&
            ((!joinOrLeaveBtnVisible && result.index === 0) || (joinOrLeaveBtnVisible && result.index === 1))
          ) {
            openChallengeEditPage()
          }
          if (
            isAdmin &&
            ((!joinOrLeaveBtnVisible && result.index === 1) || (joinOrLeaveBtnVisible && result.index === 2))
          ) {
            toggleDeleteGoalModal()
          }
        }
      }

      showActions()
    } catch (e) {
      return toggleContextMenu()
    }
  }

  const openChallengeEditPage = (event?: any) => {
    if (event) event.preventDefault()
    store.dispatch(push(`/challenges/${goalId}/edit`))
  }

  const showOnlyJoinButton = !participating && !isAdmin && challengeHasNotEnded

  const preventRedirection = (event: any) => event.stopPropagation()

  return !actionsVisible ? (
    <></>
  ) : (
    <div className={className} onClick={preventRedirection}>
      {!showOnlyJoinButton && (
        <>
          <div className="yum-goal-card-context-menu">
            <ul
              ref={menuRef}
              onKeyDown={onMenuKeyDown}
              className={`yum-goal-card-context-menu__dropdown ${
                contextMenuVisible ? 'yum-goal-card-context-menu__dropdown--open' : ''
              }`}
              style={!isListView ? { top: '30px' } : { top: 'auto', bottom: '10px' }}
              {...(!contextMenuVisible && { inert: '' })}
            >
              {showJoinBtn && (
                <li>
                  <button
                    style={{ color: darkGreen }}
                    className="yum-goal-card-context-menu__link"
                    type="button"
                    onClick={onJoinActionClicked}
                  >
                    <AddIcon className="yum-goal-card-context-menu__icon" />
                    {t`Join`}
                  </button>
                </li>
              )}
              {showLeaveBtn && (
                <li>
                  <button className="yum-goal-card-context-menu__link" type="button" onClick={onLeaveGoalClicked}>
                    <LogOutIcon className="yum-goal-card-context-menu__icon" />
                    {t`Leave challenge`}
                  </button>
                </li>
              )}
              {isAdmin && (
                <>
                  <li>
                    <button className="yum-goal-card-context-menu__link" type="button" onClick={openChallengeEditPage}>
                      <EditIcon className="yum-goal-card-context-menu__icon" style={{ color: primaryBlue }} />
                      {t`Edit challenge`}
                    </button>
                  </li>
                  <li>
                    <button
                      className="yum-goal-card-context-menu__link"
                      type="button"
                      onClick={onDeleteGoalClicked}
                      style={{ color: red }}
                    >
                      <DeleteIcon className="yum-goal-card-context-menu__icon" />
                      {t`Delete challenge`}
                    </button>
                  </li>
                </>
              )}
            </ul>
            {contextMenuVisible && <div className="yum-goal-card-context-menu__backdrop" onClick={toggleContextMenu} />}
          </div>

          <YumDialog open={leaveGoalModalVisible} alert>
            <YumDialogTitle>{title}</YumDialogTitle>
            <YumDialogContent>
              <YumDialogContentText>{t`Are you sure you want to leave this challenge?`}</YumDialogContentText>
            </YumDialogContent>
            <YumDialogActions>
              <YumCancelButton onClick={toggleLeaveGoalModal} />
              <YumSubmitButton text={t`Leave`} onClick={onLeaveGoalConfirmed} />
            </YumDialogActions>
          </YumDialog>

          {isAdmin && (
            <YumDialog open={deleteGoalModalVisible} alert>
              <YumDialogTitle>{t`Delete challenge`}</YumDialogTitle>
              <YumDialogContent>
                <YumDialogContentText>
                  {jt`Are you sure you want to delete challenge ${challengeName} for everyone? Once deleted, you can't undo it.`}
                </YumDialogContentText>
              </YumDialogContent>
              <YumDialogActions>
                <YumCancelButton onClick={toggleDeleteGoalModal} />
                <YumSubmitButton text={t`Delete`} onClick={onDeleteGoalConfirmed} color="error" />
              </YumDialogActions>
            </YumDialog>
          )}
        </>
      )}

      {showOnlyJoinButton ? (
        <YumButton
          size="small"
          color="secondary"
          onClick={onJoinActionClicked}
          disabled={joinInProgress}
        >{t`Join`}</YumButton>
      ) : (
        <>
          {isListView ? (
            <button
              type="button"
              className="yum-goal-card__actions-button yum-goal-card__actions-button--context-menu"
              onClick={onContextMenuClicked}
              title={t`More`}
              aria-label={t`More`}
              aria-expanded={contextMenuVisible}
              style={{ cursor: 'pointer' }}
            >
              <MoreIcon style={{ width: '1rem', height: '1rem' }} />
            </button>
          ) : (
            <YumButton
              onClick={onContextMenuClicked}
              variant="outlined"
              size="small"
              title={t`More`}
              aria-label={t`More`}
              aria-expanded={contextMenuVisible}
            >
              <MoreIcon style={{ width: '1rem', height: '1rem' }} />
            </YumButton>
          )}
        </>
      )}
    </div>
  )
}
